import {
  getTool,
  getToolNames,
  type ProjectSchemaJSON,
  patchSchema,
  type ToolExecutionGraphQLJSON
} from '@takeshape/schema';
import { type CreateFormStoreOptions, createFormStore } from '@takeshape/shape-form';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import { useIntrospectedSchema } from 'app/branches/hooks/use-introspected-schema';
import { showMessage } from 'app/messages/actions';
import set from 'lodash/fp/set.js';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStore } from 'zustand';
import { TOOL_WORKING_COPY_ORIGIN } from '../../constants';
import { useToolStore } from '../../tool-store.ts';
import type { ToolEditorFormState } from '../../types.js';
import { resolveToolQuery } from '../../utils/convert-tool-query.js';
import { getSchemaUpdate } from '../../utils.js';
import {
  createToolEditorValidator,
  toolEditorFormConfig,
  toolEditorSchema,
  toolEditorWidgets
} from './tool-editor-form.js';

export type ToolEditorHookProps = {
  toolName?: string;
};

function normalize(
  runtimeSchemaJSON: ProjectSchemaJSON,
  execution: ToolExecutionGraphQLJSON
): ToolExecutionGraphQLJSON & { query: string } {
  let query = '';

  if (typeof execution.query === 'string') {
    query = execution.query;
  } else {
    try {
      query = resolveToolQuery(runtimeSchemaJSON, execution.query);
    } catch (e) {
      console.error('Failed to convert tool query ref to string:', e, 'query config:', execution.query);
    }
  }

  return { ...execution, query };
}

export const useToolEditor = (props: ToolEditorHookProps) => {
  const { toolName } = props;

  const dispatch = useDispatch();
  const [currentToolName, setCurrentToolName] = useState<string | undefined>(toolName);
  const {
    activeSchemaCompat: workingCopy,
    activeRuntimeSchemaJSON,
    update: updateWorkingCopy,
    hasPendingChanges,
    setHasPendingChanges
  } = useActiveSchema();
  const runEditPath = useToolStore((state) => state.runEditPath);
  const closeModal = useToolStore((state) => state.closeModal);
  const { schema: graphqlSchema } = useIntrospectedSchema();
  const graphqlSchemaRef = useRef(graphqlSchema);
  graphqlSchemaRef.current = graphqlSchema;

  const showError = useCallback((message: string) => dispatch(showMessage('error', message)), [dispatch]);

  // Compute initial data from the working copy
  const initialData: Partial<ToolEditorFormState> = useMemo(() => {
    if (!workingCopy || !activeRuntimeSchemaJSON) return {};

    const rawTool = toolName ? getTool(workingCopy, toolName) : undefined;
    let formState: Partial<ToolEditorFormState> = {};
    if (rawTool) {
      const { execution, ...rest } = rawTool;
      formState = { ...rest };
      if (execution.type === 'graphql') {
        formState.execution = normalize(activeRuntimeSchemaJSON, execution);
      }
    }

    return formState;
  }, [toolName, workingCopy, activeRuntimeSchemaJSON]);

  // Apply schema update to working copy
  const applySchemaUpdate = useCallback(
    (data: ToolEditorFormState) => {
      if (!workingCopy || !data.name) return;

      try {
        const { toolName: newToolName, schemaUpdate } = getSchemaUpdate(workingCopy, data, currentToolName);
        let patched = patchSchema(workingCopy, schemaUpdate);

        if (runEditPath) {
          patched = set(runEditPath, `Tool.${newToolName}`, patched);
        }
        setCurrentToolName(newToolName);
        void updateWorkingCopy(patched, TOOL_WORKING_COPY_ORIGIN);
      } catch (e) {
        console.trace('Error updating working copy in tool editor', e);
        showError((e as Error).message);
        setHasPendingChanges(false);
      }
    },
    [workingCopy, currentToolName, updateWorkingCopy, showError, setHasPendingChanges, runEditPath]
  );

  const handleSubmit = useCallback(
    (values: ToolEditorFormState) => {
      applySchemaUpdate(values);
      setHasPendingChanges(true);
    },
    [applySchemaUpdate, setHasPendingChanges]
  );

  // Validator
  const existingToolNames = useMemo(() => (workingCopy ? getToolNames(workingCopy) : []), [workingCopy]);
  const validator = useMemo(
    () => createToolEditorValidator(currentToolName, existingToolNames, graphqlSchemaRef),
    [currentToolName, existingToolNames]
  );

  // Create form store (once)
  // biome-ignore lint/correctness/useExhaustiveDependencies: store should only be created once
  const store = useMemo(() => {
    const options: CreateFormStoreOptions<ToolEditorFormState> = {
      name: 'toolEditor',
      schema: toolEditorSchema,
      defaultValues: initialData as ToolEditorFormState,
      config: toolEditorFormConfig,
      onSubmit: async (values) => handleSubmit(values),
      validator,
      validateOnBlur: true
    };
    return createFormStore<ToolEditorFormState>(options);
  }, []);

  const storeState = useStore(store);

  return {
    store,
    widgets: toolEditorWidgets,
    handleSubmit: async () => {
      await storeState.submit();
      if (store.getState().isValid) {
        closeModal();
      }
    },
    hasPendingChanges,
    isDisabled: !storeState.isDirty || !storeState.isValid || storeState.isValidating
  };
};

export type ToolEditorProps = ReturnType<typeof useToolEditor>;
